import React, { useState } from "react";
import GlimpseCard from "components/card/card.component";
import { IRegisterUser } from "interfaces/RegisterInterface";
import FormikSelect from "components/select/formik-select";
import FormikInput from "components/input/formik-input";
import { Formik, Form, ErrorMessage, useField } from "formik";
import InputError from "components/input-error/input-error";
import { IUserRegistration } from "interfaces/user.interface";
import { emailRegex, passwordRegex } from "constants/regex";
import {
  PRIMARY_OPTIONS,
  SECONDARY_OPTIONS,
} from "constants/education.constants";
import * as Yup from "yup";
import Button from "components/button/button";

const RegisterUser: React.FC<IRegisterUser> = ({
  userInfo,
  handleValueChanged,
  handleSubmit,
  options,
}) => {
  const validationSchema = Yup.object({
    first_name: Yup.string().required("Enter your first name."),
    last_name: Yup.string().required("Enter your last name."),
    email: Yup.string()
      .matches(emailRegex, "Enter valid email.")
      .required("Email is required"),
    password: Yup.string()
      .matches(
        passwordRegex,
        "Password should be minimum 8 characters with at least 1 number and 1 letter."
      )
      .required("Password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("password")], "Password and Confirm password must match")
      .required("Password confirmation is required"),
    dob: Yup.string().required("Enter your birth year"),
    education_stage: Yup.string().required("Please select an option"),
    extra_info: Yup.string().required("Please select an option"),
    other_text: Yup.string().when("extra_info", {
      is: "other",
      then: (schema) => schema.required("Please specify"),
      otherwise: (schema) => schema.notRequired(),
    }),
    terms: Yup.boolean().oneOf([true], "You must accept the Terms of Use and Privacy Policy"),
  });

  const [emailSubscribed, setEmailSubscribed] = useState(true);

  return (
    <>
      <div className="topic">
        <h2 className="text-display-lg text-white text-center">
          <p>Welcome!</p>{" "}
          <span className="text-display text-white">We're excited you're here</span>
        </h2>
        <p className="mt-4 text-base font-normal leading-4 text-white text-center">
          Fill out the information below to complete account set up.
        </p>
      </div>
      <div className="flex justify-center py-4 sm:w-full">
        <GlimpseCard className="bg-white max-w-[480px] pb-4">
          <div className="flex flex-col ">
            <img
              className="w-[120px] h-[100px] object-cover ml-3 self-center"
              src="/assets/icons/signup_logo.png"
              alt="Boy_With_Glass_Icon"
              loading="lazy"
            />
            <Formik
              validateOnBlur={false}
              validateOnChange={false}
              initialValues={{
                ...userInfo,
                education_stage: "",
                extra_info: "",
                other_text: "",
                terms: false,
              }}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                const payload = {
                  ...values,
                  email_subscribed: emailSubscribed,
                  extra_info:
                    values.extra_info === "other"
                      ? values.other_text
                      : values.extra_info,
                };
                handleSubmit(payload);
              }}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <div className="container p-2 mx-auto">
                    {/* --- Name fields --- */}
                    <div className="flex gap-2 mt-3">
                      <div className="w-1/2">
                        <FormikInput
                          id="first_name"
                          name="first_name"
                          label="First Name"
                          type="input"
                          placeholder="First Name*"
                          maxLength={50}
                        />
                      </div>
                      <div className="w-1/2">
                        <FormikInput
                          id="last_name"
                          name="last_name"
                          label="Last Name"
                          type="input"
                          placeholder="Last Name*"
                          maxLength={50}
                        />
                      </div>
                    </div>

                    {/* --- Email --- */}
                    <div className="w-full mt-4">
                      <FormikInput
                        type="email"
                        label="Email"
                        as="input"
                        id="email"
                        name="email"
                        placeholder="Email*"
                        maxLength={50}
                      />
                    </div>

                    {/* --- DOB --- */}
                    <div className="w-full mt-4">
                      <FormikSelect
                        name="dob"
                        options={options || []}
                        showLabel={true}
                        label="Birth Year"
                        id="dob"
                        placeholder="Select.."
                        onChange={(value, fieldName) => handleValueChanged({ target: { name: fieldName, value } } as React.ChangeEvent<HTMLSelectElement>)}
                      />
                    </div>

                    {/* --- Primary Dropdown --- */}
                    <div className="w-full mt-4">
                      <FormikSelect
                        name="education_stage"
                        options={PRIMARY_OPTIONS}
                        showLabel={true}
                        label="Which of these describes you best?"
                        id="education_stage"
                        placeholder="Select.."
                        onChange={(val) => {
                          setFieldValue("education_stage", val);
                          setFieldValue("extra_info", ""); // reset secondary
                          setFieldValue("other_text", ""); // reset other_text
                        }}
                      />
                    </div>

                    {/* --- Secondary Dropdown --- */}
                    {values.education_stage && (
                      <div className="w-full mt-4">
                        <FormikSelect
                          name="extra_info"
                          options={SECONDARY_OPTIONS[values.education_stage]}
                          showLabel={true}
                          label={`${
                            PRIMARY_OPTIONS.find(
                              (option) =>
                                option.value === values.education_stage
                            )?.label || ""
                          }, and`}
                          id="extra_info"
                          placeholder="Select..."
                          onChange={(val) => {
                            setFieldValue("extra_info", val);
                            if (val !== "other")
                              setFieldValue("other_text", "");
                          }}
                        />
                      </div>
                    )}

                    {/* --- "Other" free text --- */}
                    {values.extra_info === "other" && (
                      <div className="w-full mt-4">
                        <FormikInput
                          name="other_text"
                          id="other_text"
                          label="Please specify"
                          type="text"
                          placeholder="Type here..."
                          maxLength={200}
                        />
                      </div>
                    )}

                    {/* --- Passwords --- */}
                    <div className="w-full mt-4">
                      <div>
                        <FormikInput
                          type="password"
                          id="password"
                          label="New Password"
                          as="input"
                          name="password"
                          placeholder="New Password"
                          isPassword={true}
                          maxLength={50}
                        />
                      </div>
                      <div>
                        <FormikInput
                          type="password"
                          id="confirm_password"
                          label="Confirm New Password"
                          as="input"
                          name="confirm_password"
                          placeholder="Confirm New Password"
                          isPassword={true}
                          maxLength={50}
                        />
                      </div>
                    </div>

                  {/* --- Terms --- */}
                  <div className="mt-4">
                    <div className="flex items-start gap-2">
                      <input
                        id="terms_checkbox"
                        type="checkbox"
                        name="terms"
                        checked={values.terms}
                        onChange={(e) => setFieldValue("terms", e.target.checked)}
                        className="w-4 h-4 mt-0.5 shrink-0 rounded border-border text-action focus:ring-action focus:ring-2 cursor-pointer"
                      />
                      <label
                        htmlFor="terms_checkbox"
                        className="text-body-sm text-text-heading cursor-pointer"
                      >
                        I agree to the CareerSight{" "}
                        <a
                          href="https://www.careersight.com/terms-of-use"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline cursor-pointer text-action underline-offset-2"
                        >
                          Terms of Use
                        </a>
                        {" "}and{" "}
                        <a
                          href="https://www.careersight.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline cursor-pointer text-action underline-offset-2"
                        >
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                    <ErrorMessage name="terms" component={InputError} />
                  </div>

                  {/* --- Email Subscribe --- */}
                  <div className="flex items-start gap-2 mt-2">
                    <input
                      id="subscribe_checkbox"
                      type="checkbox"
                      checked={emailSubscribed}
                      onChange={(e) => setEmailSubscribed(e.target.checked)}
                      className="w-4 h-4 mt-0.5 shrink-0 rounded border-border text-action focus:ring-action focus:ring-2 cursor-pointer"
                    />
                    <label
                      htmlFor="subscribe_checkbox"
                      className="text-body-sm text-text-heading cursor-pointer"
                    >
                      <span>I agree to receive email updates from CareerSight</span>
                    </label>
                  </div>

                  {/* --- Submit --- */}
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full mt-6"
                  >
                    Create Account
                  </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </GlimpseCard>
      </div>
    </>
  );
};

export default RegisterUser;
